import {
    createInstance,
    ReactSDKClient,
    OptimizelyDecision
} from '@optimizely/react-sdk';

type Variables = {
    variable: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
}[];

export type OptimizelyOptions = {
    experiments?: {
        name: string;
        variation: string;
    }[];
    features?: {
        feature: string;
        isEnabled: boolean;
        variables?: Variables;
    }[];
};

const parseVariables = (variables: Variables) => {
    return variables.reduce((acc, curr) => {
        acc[curr.variable] = curr.value;
        return acc;
    }, {} as OptimizelyDecision['variables']);
};

export const getMockOptimizelyClient = (
    options?: OptimizelyOptions
): ReactSDKClient => {
    const client = createInstance({});

    /* underlying call in useDecision hook aka. useFeatureFlag */
    client.decide = (featureKey) => {
        const override = options?.features?.find(
            ({ feature }) => feature === featureKey
        );
        const parsedVariables = override?.variables
            ? parseVariables(override.variables)
            : {};
        return {
            variationKey: override?.feature || null,
            enabled: !!override?.isEnabled,
            variables: parsedVariables,
            ruleKey: null,
            flagKey: override?.feature || featureKey,
            reasons: [],
            userContext: {
                id: null
            }
        };
    };

    /* underlying call in useExperiment hook */
    client.activate = (experimentKey) =>
        options?.experiments?.find(({ name }) => name === experimentKey)
            ?.variation ?? null;

    client.isFeatureEnabled = (featureKey) =>
        options?.features?.find(({ feature }) => feature === featureKey)
            ?.isEnabled ?? false;

    client.getFeatureVariableBoolean = (featureKey, variableKey) =>
        options?.features
            ?.find(({ feature }) => feature === featureKey)
            ?.variables?.find(({ variable }) => variable === variableKey)
            ?.value ?? null;

    return client;
};
